import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  AppBar, AppBarProps, Button, ButtonProps, Collapse, styled, Toolbar, Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { IAppReduxState } from '../../models';
import { AuthService, SessionService, UtilService } from '../../services';
import environment from '../../environments/environment';
import { updateCurrentTime } from '../../reducers/core-reducer';

const TimeOutAppBar = styled(AppBar)<AppBarProps>(({ theme }) => ({
  background: theme.palette.yellowPlan.main,
  color: theme.palette.mainBackground.contrastText,
  alignItems: 'center',
}));

const RefreshButton = styled(Button)<ButtonProps>(({ theme }) => ({
  marginLeft: '10px',
  fontSize: 'inherit',
  background: theme.palette.greenPlan.contrastText,
  color: theme.palette.mainBackground.contrastText,
}));

function TimeOutBar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showTimeOut, setShowTimeOut] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const isAuthenticated = useSelector<IAppReduxState, boolean>((x) => x.core.isAuthenticated);
  const currentTime = useSelector<IAppReduxState, number>((x) => x.core.currentTime);
  const tokenExp = useSelector<IAppReduxState, number>((x) => x.core.tokenExp);
  const refreshToken = useSelector<IAppReduxState, string>((x) => x.core.refreshToken);

  const refreshSession = useCallback(
    async () => {
      const userRes = await AuthService.refreshToken(refreshToken);
      SessionService.signIn(userRes);
      setShowTimeOut(false);
    },
    [refreshToken],
  );

  const diff = useMemo(() => tokenExp - seconds, [seconds, tokenExp]);
  const countDown = useMemo(() => {
    if (diff <= 0) {
      return {
        m: 0,
        s: 0,
      };
    }
    return UtilService.secondsToTime(diff);
  }, [diff]);

  const incrementTimer = useCallback(() => setSeconds(seconds + 1), [seconds]);

  const visibilityCheck = useCallback(() => {
    if (!document.hidden) {
      dispatch(updateCurrentTime(UtilService.dateToUtc(new Date())));
    }
  }, [dispatch]);

  useEffect(() => setSeconds(currentTime), [currentTime]);

  useEffect(() => {
    if (!isAuthenticated) return () => {};

    const timer = setInterval(incrementTimer, 1000);

    return () => clearInterval(timer);
  }, [incrementTimer, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      document.addEventListener('visibilitychange', visibilityCheck);
    } else {
      document.removeEventListener('visibilitychange', visibilityCheck);
    }
  }, [isAuthenticated, visibilityCheck]);

  useEffect(() => {
    if (!isAuthenticated) {
      setShowTimeOut(false);
      return;
    }

    if (seconds > tokenExp) {
      setShowTimeOut(false);
      SessionService.signOut();
      navigate('/login');
      return;
    }

    if (diff <= environment.sessionTimeout && diff !== 0) {
      setShowTimeOut(true);
    }
  }, [diff, isAuthenticated, navigate, seconds, tokenExp]);

  return (
    <Collapse in={showTimeOut}>
      <TimeOutAppBar position="fixed" elevation={0} data-testid="timeout-bar">
        <Toolbar>
          <Typography variant="h6">
            <FormattedMessage
              id="session-timeout-message"
              values={{
                mins: countDown.m,
                secs: countDown.s,
              }}
            />
          </Typography>
          <RefreshButton variant="contained" color="secondary" onClick={refreshSession}>
            <FormattedMessage id="session-timeout-refresh-button" />
          </RefreshButton>
        </Toolbar>
      </TimeOutAppBar>
    </Collapse>
  );
}

export { TimeOutBar };
